import { useContext, useState } from 'react';
import { shallowEqual, useDispatch } from 'react-redux';

import { Anchor, Box, Button, Icon, IconButton, Menu, Paragraph, Table, Text } from '@zillow/constellation-10';
import {
  IconCheckmarkCircleFilled,
  IconHideFilled,
  IconHourglassFilled,
  IconMoreFilled,
  IconWarningFilled,
  IconZillowMark,
} from '@zillow/constellation-icons';

import {
  AccountViewListingOptionType,
  logError,
  RentalManagerUserPropertyDetailsSyndicationOverviewStatus,
  stringHelper,
  useRentalManagerUserPropertyWebAnalyticsWebApiHandler_get,
} from '@zg-rentals/rental-manager-env';
import { LoadingText } from '@zg-rentals/loading-components';

import { useSelector } from '../../../../selectors/useSelector';
import { AppSelectors } from '../../../../selectors';

import {
  useZramAdvertising,
  useZramListingQualityScore,
  useZramOptions,
  useZramProspects,
  useZramSources,
  useZramStatus,
} from '../../../../hooks/queries/zram';

import { getPhotoUrls } from '../../../../utils/propertiesPageUtils';
import propertyUtils from '../../../../utils/propertyUtils';

import { RMWContext } from '../../../../providers/RMWContext';
import { RMW_SUB_PATH } from '../../../../pathsManifest';
import PropertiesActions from '../../../../actions/PropertiesActions';

import { RecsLearnMoreConstants } from '../../../../constants/RecommendationsConstants';
import QueryConstants from '../../../../constants/QueryConstants';
import PropertiesPageCSEvents from '../../../../model/ClickstreamEvent/PropertiesPageCSEvents';
import {
  ClaimDashboardIncreaseYourExposureEvent,
  ClaimDashboardReleaseClaimEvent,
} from '../../../../model/ClickstreamEvent/ClaimDashboardEvents';
import ZRAMPropertiesPageCSEvents from '../../../../model/ClickstreamEvent/ZRAMPropertiesPageCSEvents';

import ReleaseClaimModal from '../../../../components/Modal/ReleaseClaimModal';
import ArchivePropertyModal from '../../../../components/Modal/ArchivePropertyModal';

import MediumIllustrationHouse2x from '../../../../images/MediumIllustrationHouse@2x.png';

import {
  AddressText,
  addressWrapperStyles,
  HiddenMobileTableCell,
  leftDataCellStyles,
  listingQualityContainerStyles,
  listingQualityContentRecipe,
  photoStyles,
  rightDataCellStyles,
  statusIconStyles,
} from '../styles';

import type {
  AccountViewListingDto,
  AccountViewListingOptionDto,
  AccountViewListingStatusOverviewDto,
} from '@zg-rentals/ts-rental-manager-api-client';

export const PropertyRow = ({
  listing,
  isScrolledToLeft,
  isLoading: isComponentLoading,
  isArchivedView,
}: {
  listing: AccountViewListingDto;
  isScrolledToLeft: boolean;
  isLoading: boolean;
  isArchivedView: boolean;
}) => {
  const { paths } = useContext(RMWContext);
  const dispatch = useDispatch();

  const { isScreenXxlGTE, user } = useSelector(
    (state) => ({
      isScreenXxlGTE: AppSelectors.makeIsScreenXxlGTE(state),
      user: state.user,
    }),
    shallowEqual,
  );
  const isRunningInProd = useSelector((state) => AppSelectors.makeIsRunningInProd(state));

  const [displayReleaseClaimModal, setDisplayReleaseClaimModal] = useState(false);
  const [displayArchivePropertyModal, setDisplayArchivePropertyModal] = useState('');

  const { address, name, photoKey, isMultifamily, listingAlias } = listing;

  const { data: statusData, isPending: isStatusPending } = useZramStatus(
    {
      listingAlias,
    },
    { enabled: Boolean(listingAlias) },
  );
  const isListingInactive = statusData?.status === RentalManagerUserPropertyDetailsSyndicationOverviewStatus.INACTIVE;

  const { data: listingQualityScore, isFetching: isListingQualityScoreFetching } = useZramListingQualityScore(
    {
      listingAlias,
    },
    { enabled: Boolean(listingAlias) && !isArchivedView && !(isStatusPending || isListingInactive) },
  );

  const { data: prospects, isFetching: isProspectsFetching } = useZramProspects(
    {
      listingAlias,
    },
    {
      enabled: Boolean(listingAlias) && !isArchivedView && !(isStatusPending || isListingInactive),
    },
  );

  const { data: advertising, isFetching: isAdvertisingFetching } = useZramAdvertising(
    {
      listingAlias,
    },
    { enabled: Boolean(listingAlias) && !isArchivedView },
  );

  const { data: sources, isPending: isSourcesPending } = useZramSources(
    {
      listingAlias,
    },
    { enabled: Boolean(listingAlias) },
  );
  const { data: { options = [] } = {}, isPending: isOptionsPending } = useZramOptions(
    {
      listingAlias,
    },
    { enabled: Boolean(listingAlias) },
  );
  const { data: webAnalyticsData } = useRentalManagerUserPropertyWebAnalyticsWebApiHandler_get(
    {
      listingAlias,
    },
    { enabled: Boolean(listingAlias) },
  );

  const { formattedStreet, formattedCityStateZip } = propertyUtils.formatAddress({
    ...address,
  });

  const baseRoute = `${paths.propertiesPage}/${listingAlias}`;
  const targetListingRoute = isMultifamily ? `${baseRoute}/listing` : `${baseRoute}/${RMW_SUB_PATH.DASHBOARD}/overview`;

  const adProductDataEventId =
    advertising?.buttonText === 'Increase your exposure'
      ? ClaimDashboardIncreaseYourExposureEvent.data_event_id
      : undefined;

  const routerHandler = (pathToRoute: string, openNewTab = false) => {
    if (openNewTab) {
      window.router.updateLocation(pathToRoute, '_blank');
    } else {
      window.router.transitionTo(pathToRoute);
    }
  };

  const handleInnerCTAClick = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
  };

  const handleAdvertisingCTAClick = (ctaText: string) => {
    if (ctaText === 'Increase your exposure') {
      ClaimDashboardIncreaseYourExposureEvent({ user });
    }
    ZRAMPropertiesPageCSEvents.ChangeAdProduct({ user });
  };

  const handleMenuClick = (open: boolean) => {
    if (open) {
      PropertiesPageCSEvents.PropertyCardOverflowMenu({
        user,
        currentProperty: webAnalyticsData ?? {},
      });
    }
  };

  const handleMenuItemClick = (
    event: React.MouseEvent<HTMLElement>,
    option?: AccountViewListingOptionDto,
    ctaText?: string,
  ) => {
    handleInnerCTAClick(event);

    if (!option && ctaText) {
      handleAdvertisingCTAClick(ctaText);
      return;
    }

    if (
      option?.type === AccountViewListingOptionType.ARCHIVE ||
      option?.type === AccountViewListingOptionType.UNARCHIVE
    ) {
      setDisplayArchivePropertyModal(option?.type as AccountViewListingOptionType);
      return;
    }

    if (option?.type === AccountViewListingOptionType.RELEASE_CLAIM) {
      setDisplayReleaseClaimModal(true);
      ClaimDashboardReleaseClaimEvent({ user });
      return;
    }

    if (option?.type === AccountViewListingOptionType.OVERVIEW) {
      dispatch(PropertiesActions.updateCurrentProperty({ ...address }));
    }
  };

  const handleRowClick = (event: React.MouseEvent<HTMLElement>) => {
    const openNewTab = event.ctrlKey || event.metaKey;
    event.preventDefault();
    event.stopPropagation();

    ZRAMPropertiesPageCSEvents.PropertyCardClickZRAM({ user });

    dispatch(PropertiesActions.updateCurrentProperty({ ...address }));

    if (targetListingRoute) {
      routerHandler(targetListingRoute, openNewTab);
    }
  };

  const renderCellOrError = <T,>(
    data: T | undefined | null,
    isCellPending: boolean,
    renderContent: (data: T, showLoading: boolean) => React.ReactNode,
  ) => {
    const shouldShowLoading = isComponentLoading || isCellPending;
    if (!data && !shouldShowLoading) {
      return <Text textStyle="body-sm">-</Text>;
    }
    return renderContent(data as T, shouldShowLoading);
  };

  const renderMissingDetail = (missingDetail: string) => {
    if (missingDetail in RecsLearnMoreConstants) {
      return (
        <Box key={`${name}-${missingDetail}`}>
          <Anchor
            data-eventid={ZRAMPropertiesPageCSEvents.AddImprovementClick.data_event_id}
            href={`${baseRoute}/listing?${QueryConstants.SCROLL_TO}=${missingDetail}`}
            css={{ color: 'text.neutral' }}
            onClick={(event) => {
              handleInnerCTAClick(event);
              ZRAMPropertiesPageCSEvents.AddImprovementClick({ user });
            }}
          >
            {RecsLearnMoreConstants[missingDetail].heading}
          </Anchor>
        </Box>
      );
    }
    logError({
      error: new Error(`Unknown missing detail type: ${missingDetail}`),
      errorType: 'PropertyRow.MissingDetailError',
      context: { listingAlias },
    });
    return null;
  };

  const getStatusIcon = (status: RentalManagerUserPropertyDetailsSyndicationOverviewStatus) => {
    switch (status) {
      case RentalManagerUserPropertyDetailsSyndicationOverviewStatus.WAITLISTED:
        return (
          <Icon size="sm" css={{ color: 'icon.muted', ...statusIconStyles }}>
            <IconHourglassFilled />
          </Icon>
        );
      case RentalManagerUserPropertyDetailsSyndicationOverviewStatus.DECLINED:
        return (
          <Icon size="sm" css={{ color: 'icon.express.empower.hero', ...statusIconStyles }}>
            <IconWarningFilled />
          </Icon>
        );
      case RentalManagerUserPropertyDetailsSyndicationOverviewStatus.INACTIVE:
        return (
          <Icon size="sm" css={{ color: 'icon.muted', ...statusIconStyles }}>
            <IconHideFilled />
          </Icon>
        );
      default:
        return (
          <Icon size="sm" css={{ color: 'icon.express.insight.hero', ...statusIconStyles }}>
            <IconCheckmarkCircleFilled />
          </Icon>
        );
    }
  };

  const renderStatusCell = (statusData: AccountViewListingStatusOverviewDto, showLoading: boolean) => {
    const { status, display, explanation } = statusData || {};

    return (
      <Box css={{ display: 'flex' }}>
        {status && getStatusIcon(status)}
        <Box>
          <LoadingText isLoading={showLoading} textStyle="body-sm-bold" loadingWidth="7rem">
            {display}
          </LoadingText>
          {explanation && <Paragraph textStyle="body-sm">{explanation}</Paragraph>}
        </Box>
      </Box>
    );
  };

  const renderAdvertisingCTA = (isFromActionsMenu = true) => (
    <Button
      emphasis="outlined"
      size="sm"
      tone="neutral"
      asChild={true}
      onClick={(event) => {
        handleInnerCTAClick(event);
        handleAdvertisingCTAClick(advertising?.buttonText as string);
      }}
      data-eventid={ZRAMPropertiesPageCSEvents.ChangeAdProduct.data_event_id}
      css={{
        width: isFromActionsMenu ? 'max-content' : 'fit-content',
        marginTop: isFromActionsMenu ? undefined : 'tighter',
      }}
    >
      <a href={advertising?.buttonUrl} data-eventid={adProductDataEventId} target="_blank" rel="noreferrer">
        {advertising?.buttonText}
      </a>
    </Button>
  );

  return (
    <>
      <Anchor href={targetListingRoute} onClick={handleRowClick} css={{ textDecoration: 'none', display: 'contents' }}>
        <Table.Row
          onClick={handleRowClick}
          data-eventid={ZRAMPropertiesPageCSEvents.PropertyCardClickZRAM.data_event_id}
        >
          <Table.Cell css={leftDataCellStyles(isScrolledToLeft)}>
            <Box css={{ display: 'flex' }}>
              <Box
                data-loading={isComponentLoading || null}
                css={{
                  backgroundSize: photoKey ? 'cover' : 'contain',
                  ...photoStyles,
                }}
                style={{
                  ...(isComponentLoading
                    ? {}
                    : {
                        backgroundImage: photoKey
                          ? `url(${getPhotoUrls(isRunningInProd, photoKey).thumb})`
                          : `url(${MediumIllustrationHouse2x})`,
                      }),
                }}
              />
              <Box css={addressWrapperStyles}>
                <AddressText textStyle="body-sm-bold" isLoading={isComponentLoading} loadingWidth="12rem">
                  {name}
                </AddressText>
                {isMultifamily && (
                  <AddressText textStyle="body-sm" isLoading={isComponentLoading} loadingWidth="12rem">
                    {formattedStreet}
                  </AddressText>
                )}
                <AddressText textStyle="body-sm" isLoading={isComponentLoading} loadingWidth="10rem">
                  {formattedCityStateZip}
                </AddressText>
                {Boolean(advertising?.buttonText && !isScreenXxlGTE && !isArchivedView) && renderAdvertisingCTA(false)}
              </Box>
            </Box>
          </Table.Cell>
          <HiddenMobileTableCell>
            {renderCellOrError(statusData, isStatusPending, renderStatusCell)}
          </HiddenMobileTableCell>
          {!isArchivedView && (
            <>
              {!user.isSingleFamilyEnterpriseCustomer && (
                <>
                  <HiddenMobileTableCell>
                    {isListingInactive
                      ? null
                      : renderCellOrError(
                          listingQualityScore,
                          isListingQualityScoreFetching,
                          (listingQualityScore, showLoading) => (
                            <Box css={listingQualityContainerStyles}>
                              <LoadingText
                                textStyle="body-sm"
                                css={listingQualityContentRecipe.raw({ rating: listingQualityScore?.rating })}
                                isLoading={showLoading}
                                loadingWidth="1200"
                              >
                                {listingQualityScore?.score}%
                              </LoadingText>
                            </Box>
                          ),
                        )}
                  </HiddenMobileTableCell>
                  <HiddenMobileTableCell css={{ verticalAlign: 'middle' }}>
                    {isListingInactive
                      ? null
                      : renderCellOrError(
                          listingQualityScore,
                          isListingQualityScoreFetching,
                          (listingQualityScore, showLoading) => (
                            <>
                              {listingQualityScore?.missingDetails?.length > 0 ? (
                                listingQualityScore.missingDetails.map(renderMissingDetail)
                              ) : (
                                <LoadingText isLoading={showLoading} textStyle="body-sm">
                                  -
                                </LoadingText>
                              )}
                            </>
                          ),
                        )}
                  </HiddenMobileTableCell>
                </>
              )}
              <HiddenMobileTableCell>
                {isListingInactive
                  ? null
                  : renderCellOrError(prospects, isProspectsFetching, (prospects, showLoading) => (
                      <LoadingText isLoading={showLoading} textStyle="body-sm">
                        {prospects?.count ? prospects.display : '-'}
                      </LoadingText>
                    ))}
              </HiddenMobileTableCell>
              <HiddenMobileTableCell>
                {isListingInactive
                  ? null
                  : renderCellOrError(advertising, isAdvertisingFetching, (advertising, showLoading) => (
                      <>
                        <LoadingText isLoading={showLoading} textStyle="body-sm">
                          {advertising?.title || '-'}
                        </LoadingText>
                        {advertising?.subTitle && <Paragraph textStyle="body-sm">{advertising?.subTitle}</Paragraph>}
                      </>
                    ))}
              </HiddenMobileTableCell>
            </>
          )}
          <HiddenMobileTableCell>
            {renderCellOrError(sources, isSourcesPending, (sources, showLoading) => (
              <>
                {sources?.source === 'Zillow Rental Manager' && (
                  <Icon css={{ marginRight: '100' }}>
                    <IconZillowMark />
                  </Icon>
                )}
                <LoadingText
                  isLoading={showLoading}
                  textStyle={user.isSingleFamilyEnterpriseCustomer ? 'body-sm-bold' : 'body-sm'}
                >
                  {sources?.source}
                </LoadingText>
                {sources?.additionalSources.length > 0 && (
                  <Text
                    textStyle={user.isSingleFamilyEnterpriseCustomer ? 'body-sm-bold' : 'body-sm'}
                  >{` (${stringHelper.pluralize(sources?.additionalSources.length, 'other', 'others')})`}</Text>
                )}
                {sources?.activityMessage && <Paragraph textStyle="body-sm">{sources?.activityMessage}</Paragraph>}
              </>
            ))}
          </HiddenMobileTableCell>
          <Table.Cell css={rightDataCellStyles}>
            <Box css={{ display: 'flex', gap: '200', alignItems: 'center', justifyContent: 'space-between' }}>
              {Boolean(advertising?.buttonText && isScreenXxlGTE && !isArchivedView) && renderAdvertisingCTA()}
              <Menu
                onOpenChange={handleMenuClick}
                trigger={
                  <IconButton
                    icon={<IconMoreFilled />}
                    title={`Actions for ${name}`}
                    tone="neutral"
                    emphasis="bare"
                    disabled={isComponentLoading || isOptionsPending}
                    onClick={(event) => {
                      event?.preventDefault();
                      event?.stopPropagation();
                    }}
                    css={{ marginLeft: 'auto', width: '1200' }}
                    data-eventid={PropertiesPageCSEvents.PropertyCardOverflowMenu.data_event_id}
                  />
                }
                content={options.map((option) => (
                  <Menu.Item
                    asChild={true}
                    key={option.type}
                    onClick={(event) => handleMenuItemClick(event, option)}
                    data-eventid={
                      option.type === AccountViewListingOptionType.RELEASE_CLAIM
                        ? ClaimDashboardReleaseClaimEvent.data_event_id
                        : undefined
                    }
                  >
                    <a
                      href={option.ctaUrl}
                      target={option.type === AccountViewListingOptionType.EDIT_LISTING ? '_self' : '_blank'}
                      rel={option.type === AccountViewListingOptionType.EDIT_LISTING ? '' : 'noreferrer'}
                    >
                      <Menu.ItemLabel>{option.ctaText}</Menu.ItemLabel>
                    </a>
                  </Menu.Item>
                ))}
              />
            </Box>
          </Table.Cell>
        </Table.Row>
      </Anchor>
      {displayReleaseClaimModal && (
        <ReleaseClaimModal
          listing={{
            ...listing,
            adproduct: { unpaidCustomerId: advertising?.unpaidCustomerId || '' },
          }}
          listingRoute={targetListingRoute}
          onClose={() => setDisplayReleaseClaimModal(false)}
        />
      )}
      {Boolean(displayArchivePropertyModal) && (
        <ArchivePropertyModal
          onClose={() => setDisplayArchivePropertyModal('')}
          isArchived={displayArchivePropertyModal !== AccountViewListingOptionType.ARCHIVE}
          listingAlias={listingAlias}
          address={address}
        />
      )}
    </>
  );
};
